import React, { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './SDGFilter.module.css';
import { SDG_GOALS, SDG_CATEGORY_LIST, formatSDGNumbers } from '../../constants/sdg';

interface SDGFilterProps {
  selectedSDGs: number[];
  onSelectionChange: (sdgs: number[]) => void;
  disabled?: boolean;
  className?: string;
}

const SDGFilter: React.FC<SDGFilterProps> = ({
  selectedSDGs = [],
  onSelectionChange,
  disabled = false,
  className = ''
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Check if all SDGs are selected
  const allSelected = selectedSDGs.length === 17;

  // Group SDGs by category for organized display
  const sdgsByCategory = useMemo(() => {
    const grouped: Record<string, typeof SDG_GOALS> = {};
    SDG_CATEGORY_LIST.forEach(category => {
      grouped[category.name] = SDG_GOALS.filter(sdg =>
        category.sdgs.includes(sdg.number)
      );
    });
    return grouped;
  }, []);

  // Toggle single SDG selection
  const handleSDGToggle = useCallback((sdgNumber: number) => {
    if (disabled) return;

    const newSelection = selectedSDGs.includes(sdgNumber)
      ? selectedSDGs.filter(n => n !== sdgNumber)
      : [...selectedSDGs, sdgNumber].sort((a, b) => a - b);

    onSelectionChange(newSelection);
  }, [selectedSDGs, onSelectionChange, disabled]);

  // Select all SDGs
  const handleSelectAll = useCallback(() => {
    if (disabled) return;
    onSelectionChange(allSelected ? [] : Array.from({ length: 17 }, (_, i) => i + 1));
  }, [allSelected, onSelectionChange, disabled]);

  // Select all SDGs in a category
  const handleCategorySelect = useCallback((categoryName: string) => {
    if (disabled) return;

    const category = SDG_CATEGORY_LIST.find(cat => cat.name === categoryName);
    const categorySDGs = category ? category.sdgs : [];

    const allCategorySelected = categorySDGs.every(n => selectedSDGs.includes(n));

    const newSelection = allCategorySelected
      ? selectedSDGs.filter(n => !categorySDGs.includes(n))
      : [...new Set([...selectedSDGs, ...categorySDGs])].sort((a, b) => a - b);

    onSelectionChange(newSelection);
  }, [selectedSDGs, onSelectionChange, disabled]);

  // Get count of selected SDGs in a category
  const getCategorySelectionCount = (categoryName: string): number => {
    return SDG_GOALS
      .filter(sdg => sdg.category === categoryName)
      .filter(sdg => selectedSDGs.includes(sdg.number))
      .length;
  };

  return (
    <div className={`${styles.sdgFilter} ${className}`}>
      <div className={styles.header}>
        <h3 className={styles.title}>
          {t('filters.sdg.title', 'SDG Filter')}
          {selectedSDGs.length > 0 && (
            <span className={styles.badge}>{selectedSDGs.length}</span>
          )}
        </h3>
        <button
          type="button"
          className={styles.toggleButton}
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? t('filters.sdg.collapse') : t('filters.sdg.expand')}
        >
          <span className={styles.toggleIcon}>
            {isExpanded ? '▼' : '▶'}
          </span>
        </button>
      </div>

      {isExpanded && (
        <div className={styles.content}>
          <div className={styles.controls}>
            <button
              type="button"
              className={`${styles.selectAllButton} ${allSelected ? styles.selected : ''}`}
              onClick={handleSelectAll}
              disabled={disabled}
            >
              {allSelected
                ? t('filters.sdg.deselectAll', 'Deselect All')
                : t('filters.sdg.selectAll', 'Select All')}
            </button>
            {selectedSDGs.length > 0 && selectedSDGs.length < 17 && (
              <button
                type="button"
                className={styles.clearButton}
                onClick={() => onSelectionChange([])}
                disabled={disabled}
              >
                {t('filters.sdg.clear', 'Clear')}
              </button>
            )}
          </div>

          {/* Category tabs */}
          <div className={styles.categoryTabs}>
            <button
              type="button"
              className={`${styles.categoryTab} ${!selectedCategory ? styles.active : ''}`}
              onClick={() => setSelectedCategory(null)}
            >
              {t('filters.sdg.allCategories', 'All')}
            </button>
            {SDG_CATEGORY_LIST.map(category => {
              const count = getCategorySelectionCount(category.name);
              const total = sdgsByCategory[category.name]?.length || 0;
              return (
                <button
                  key={category.name}
                  type="button"
                  className={`${styles.categoryTab} ${selectedCategory === category.name ? styles.active : ''}`}
                  onClick={() => setSelectedCategory(category.name)}
                  title={category.description}
                >
                  <span
                    className={styles.categoryIcon}
                    style={{ backgroundColor: category.color }}
                  />
                  {category.name}
                  {count > 0 && (
                    <span className={styles.categoryCount}>
                      {count}/{total}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* SDG grid */}
          <div className={styles.sdgGrid}>
            {(!selectedCategory ? SDG_GOALS : sdgsByCategory[selectedCategory]).map(sdg => {
              const isSelected = selectedSDGs.includes(sdg.number);
              return (
                <label
                  key={sdg.number}
                  className={`${styles.sdgItem} ${isSelected ? styles.selected : ''}`}
                  title={`${sdg.title}\n${sdg.description}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => handleSDGToggle(sdg.number)}
                    disabled={disabled}
                    className={styles.checkbox}
                    aria-label={`SDG ${sdg.number}: ${sdg.title}`}
                  />
                  <div
                    className={styles.sdgCard}
                    style={{
                      backgroundColor: isSelected ? sdg.color : undefined,
                      borderColor: sdg.color
                    }}
                  >
                    <div className={styles.sdgNumber}>{sdg.number}</div>
                    <div className={styles.sdgIcon}>{sdg.icon}</div>
                    <div className={styles.sdgTitle}>{sdg.shortTitle}</div>
                  </div>
                </label>
              );
            })}
          </div>

          {/* Category select all buttons */}
          {selectedCategory && (
            <div className={styles.categoryActions}>
              <button
                type="button"
                className={styles.categorySelectButton}
                onClick={() => handleCategorySelect(selectedCategory)}
                disabled={disabled}
              >
                {getCategorySelectionCount(selectedCategory) === sdgsByCategory[selectedCategory].length
                  ? t('filters.sdg.deselectCategory', { category: selectedCategory })
                  : t('filters.sdg.selectCategory', { category: selectedCategory })}
              </button>
            </div>
          )}

          {/* Selected SDGs summary */}
          {selectedSDGs.length > 0 && selectedSDGs.length <= 5 && (
            <div className={styles.selectedSummary}>
              <strong>{t('filters.sdg.selected', 'Selected')}:</strong>
              <span className={styles.selectedList}>
                {formatSDGNumbers(selectedSDGs)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SDGFilter;